import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Sword, Video, Shield, Zap, ChevronRight, Loader2, ChessBishop } from 'lucide-react'
import { createRoom } from '@/utils/roomApi.js'

export default function HomePage() {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  async function handleCreateRoom() {
    setCreating(true)
    setError(null)
    const { data, error: err } = await createRoom()

    if (err || !data) {
      setError('SIGNAL_INTERRUPTED')
      setCreating(false)
      return
    }
    navigate(`/room/${data.id}?role=creator`)
  }

  return (
    <div className="relative min-h-screen w-full bg-[#050505] text-zinc-100 selection:bg-orange-500 selection:text-white overflow-hidden font-sans">
      
      {/* --- BACKGROUND ENGINE --- */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,_rgba(249,115,22,0.15)_0%,_transparent_70%)]" />
        {/* Animated Grid */}
        <div 
          className="absolute inset-0 opacity-[0.15]" 
          style={{ backgroundImage: `linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)`, backgroundSize: '40px 40px' }} 
        />
        {/* Large Watermark */}
        <motion.div 
          initial={{ opacity: 0 }} animate={{ opacity: 0.03 }} transition={{ duration: 2 }}
          className="absolute inset-0 flex items-center justify-center text-[25vw] font-black pointer-events-none select-none italic"
        >
          STRATEGY
        </motion.div>
      </div>

      {/* --- MAIN UI --- */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-screen px-6">
        
        {/* Hero Section */}
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="relative mb-8"
          >
            <div className="absolute inset-0 bg-orange-600 blur-[80px] opacity-20 animate-pulse" />
            <div className="relative bg-zinc-900 border border-white/10 p-6 rounded-2xl shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500">
               <ChessBishop className="w-16 h-16 text-orange-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-7xl md:text-9xl font-black uppercase italic tracking-tighter leading-none mb-4">
              Duel<span className="text-orange-600">o</span>
            </h1>
            <p className="text-zinc-500 font-mono tracking-widest text-xs md:text-sm uppercase mb-12">
              P2P Chess with Video &bull; Chat &bull; Live reaction
            </p>
          </motion.div>

          {/* Action Card */}
          <motion.div 
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="w-full max-w-md group"
          >
            <div className="relative p-[1px] rounded-2xl overflow-hidden bg-gradient-to-b from-white/20 to-transparent">
              <div className="bg-zinc-950/90 backdrop-blur-xl p-8 rounded-[15px] relative">
                
                <div className="flex justify-between items-center mb-8">
                  <div className="text-left">
                    <p className="text-[10px] font-bold text-orange-500 uppercase tracking-widest">System Ready</p>
                    <h3 className="text-lg font-bold">Initialize Arena</h3>
                  </div>
                  <div className="flex gap-1">
                    {[1, 2, 3].map((i) => (
                      <motion.div 
                        key={i}
                        animate={{ opacity: [0.2, 1, 0.2] }}
                        transition={{ repeat: Infinity, duration: 1.5, delay: i * 0.2 }}
                        className="h-1 w-4 bg-orange-500 rounded-full"
                      />
                    ))}
                  </div>
                </div>

                <button
                  onClick={handleCreateRoom}
                  disabled={creating}
                  className="w-full relative group/btn overflow-hidden rounded-xl bg-orange-600 py-5 transition-all hover:bg-orange-500 active:scale-[0.98] disabled:opacity-50"
                >
                  <div className="relative z-10 flex items-center justify-center gap-3 text-white font-bold uppercase tracking-tight">
                    {creating ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Start Match <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                      </>
                    )}
                  </div>
                  <motion.div 
                    className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700 skew-x-12" 
                  />
                </button>

                <AnimatePresence>
                  {error && (
                    <motion.p 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-4 text-red-500 font-mono text-[10px] uppercase tracking-tighter"
                    >
                      {error}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div 
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.6 }}
             className="grid grid-cols-3 gap-12 mt-20"
          >
            <Feature icon={<Zap size={20}/>} label="Fast Link" />
            <Feature icon={<Video size={20}/>} label="Video P2P" />
            <Feature icon={<Shield size={20}/>} label="Private" />
          </motion.div>
        </div>
      </main>

      {/* Decorative HUD Elements */}
      <div className="absolute top-10 left-10 hidden lg:block border-l border-orange-500/30 pl-4 py-2">
        <p className="text-[10px] font-mono text-zinc-600 uppercase">Be5 - Nf3</p>
        <p className="text-[10px] font-mono text-zinc-600 uppercase">Bxe5 - exd5</p>
      </div>

      <div className="absolute bottom-10 right-10 hidden lg:block text-right">
        <p className="text-[10px] font-mono text-zinc-600 uppercase">v1.0.2 // Beta Release</p>
        <div className="h-[1px] w-32 bg-gradient-to-l from-orange-500/50 to-transparent mt-2" />
      </div>
    </div>
  )
}

function Feature({ icon, label }) {
  return (
    <div className="flex flex-col items-center gap-3 group">
      <div className="w-12 h-12 rounded-xl border border-white/5 flex items-center justify-center bg-zinc-900/50 group-hover:border-orange-500/50 group-hover:bg-orange-500/10 transition-all duration-300">
        <div className="text-zinc-400 group-hover:text-orange-500 transition-colors">
          {icon}
        </div>
      </div>
      <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-[0.2em]">{label}</span>
    </div>
  )
}